'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useEffect, useState } from 'react';
import { apiBase, fetchWithTimeout } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'next/navigation';
import { normalizeEventCategory, type EventCategory } from '@/lib/eventCategories';

type Item = {
  id: string;
  title: string;
  description: string | null;
  startAt: string | null;
  coverImage?: string | null;
  externalEntryUrl: string | null;
  canApplyInternally?: boolean;
  registrationMethod?: 'first_come' | 'lottery';
  isMembersOnly?: boolean;
  categoryLabel?: string | null;
  speaker?: string | null;
  speakerTitle?: string | null;
};

/**
 * 会員限定イベント一覧（クライアント取得）
 * - 目的: members フィルタはAPI側で認証必須のため、トークンを付与してCSRで取得する
 * - 仕様:
 *   - 未ログイン時: ログイン誘導を表示
 *   - ログイン時: /api/events?filter=members を Authorization: Bearer で取得
 */
export default function MembersEventsClient({
  pageSize,
  basePath,
}: {
  pageSize: number;
  basePath: string;
}) {
  const { token, loading } = useAuth();
  const sp = useSearchParams();

  const pageParam = Number(sp.get('page') ?? '1');
  const currentPage = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
  const categoryActive: EventCategory | null = normalizeEventCategory(
    sp.get('category')
  );

  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setError(null);
      if (!token) {
        setItems([]);
        setTotal(0);
        return;
      }
      setFetching(true);
      try {
        const params = new URLSearchParams();
        params.set('filter', 'members');
        if (categoryActive) params.set('category', categoryActive);
        params.set('page', String(currentPage));
        params.set('pageSize', String(pageSize));

        const res = await fetchWithTimeout(
          `${apiBase()}/api/events?${params.toString()}`,
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
            cache: 'no-store',
          },
          15000
        );
        if (!res.ok) {
          // 想定される例外:
          // - 401: 未ログイン/トークン期限切れ
          const msg = await res
            .json()
            .catch(() => ({ message: '会員限定イベントの取得に失敗しました。' }));
          throw new Error(msg?.message ?? '会員限定イベントの取得に失敗しました。');
        }
        const data = (await res.json()) as { items: Item[]; total: number };
        if (cancelled) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(typeof data.total === 'number' ? data.total : 0);
      } catch (e) {
        console.error('[会員限定イベント取得エラー]', e);
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : '会員限定イベントの取得に失敗しました。'
        );
        setItems([]);
        setTotal(0);
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token, currentPage, pageSize, categoryActive]);

  const makeHref = (p: number) => {
    const params = new URLSearchParams(sp.toString());
    params.set('filter', 'members');
    if (categoryActive) params.set('category', categoryActive);
    else params.delete('category');
    if (p > 1) params.set('page', String(p));
    else params.delete('page');
    const q = params.toString();
    return q ? `${basePath}?${q}` : basePath;
  };

  if (loading) {
    return (
      <div className='mx-auto max-w-3xl rounded border border-line bg-white p-8 text-center text-gray-800 shadow-sm'>
        <p className='text-sm text-gray-600'>ログイン状態を確認しています...</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className='mx-auto max-w-3xl rounded border border-line bg-white p-8 text-center text-gray-800 shadow-sm'>
        <p className='text-lg font-semibold'>会員限定イベントを見るにはログインが必要です。</p>
        <p className='mt-2 text-sm text-gray-600'>
          ログイン後に「会員限定」フィルタをご利用ください。
        </p>
        <div className='mt-6 flex items-center justify-center gap-3'>
          <Link
            href='/login'
            className='inline-flex h-10 items-center justify-center bg-primary px-4 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
          >
            ログイン
          </Link>
          <Link
            href='/events?filter=public'
            className='inline-flex h-10 items-center justify-center border border-secondary px-4 text-sm font-semibold text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
          >
            一般参加可を見る
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='mx-auto max-w-3xl rounded border border-line bg-white p-8 text-center text-gray-800 shadow-sm'>
        <p className='text-lg font-semibold'>会員限定イベントの取得に失敗しました。</p>
        <p className='mt-2 text-sm text-red-600' role='alert'>
          {error}
        </p>
        <div className='mt-6 flex items-center justify-center gap-3'>
          <Link
            href='/events?filter=members'
            className='inline-flex h-10 items-center justify-center border border-line bg-white px-4 text-sm font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
          >
            再読み込み
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {fetching ? (
        <div className='mx-auto max-w-3xl rounded border border-line bg-white p-8 text-center text-gray-800 shadow-sm'>
          <p className='text-sm text-gray-600'>読み込み中...</p>
        </div>
      ) : items.length === 0 ? (
        <div className='mx-auto max-w-3xl rounded border border-line bg-white p-8 text-center text-gray-800 shadow-sm'>
          <p className='text-lg font-semibold'>該当するイベントがありません。</p>
          <p className='mt-2 text-sm text-gray-600'>
            フィルタを変更するか、しばらく時間をおいてご確認ください。
          </p>
          <div className='mt-6 flex items-center justify-center gap-3'>
            <Link
              href='/events'
              className='inline-flex h-10 items-center justify-center border border-line bg-white px-4 text-sm font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
            >
              すべて表示
            </Link>
          </div>
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
          {items.map((ev, idx) => (
            <article
              key={`${ev.id}-${idx}`}
              className='overflow-hidden bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)]'
            >
              <div className='relative px-5 pt-5'>
                <div className='inline-flex h-8 items-center justify-center bg-primary px-3 text-xs font-semibold text-white'>
                  {formatMonthDayJST(ev.startAt ? new Date(ev.startAt) : null)}
                </div>
                {ev.isMembersOnly && (
                  <div className='absolute right-5 top-6 inline-flex h-6 items-center justify-center bg-secondary px-3 text-[10px] font-semibold text-white'>
                    会員限定
                  </div>
                )}
              </div>
              <div className='relative mt-3 h-[207px] w-full bg-gray-100'>
                <Image
                  src={
                    ev.coverImage ??
                    [
                      '/assets/img/event-card-1.png',
                      '/assets/img/event-card-2.png',
                      '/assets/img/event-card-3.png',
                    ][idx % 3]
                  }
                  alt={`${ev.title} の画像`}
                  fill
                  className='object-cover'
                  sizes='(min-width: 768px) 33vw, 100vw'
                  quality={90}
                />
              </div>
              <div className='space-y-4 p-5'>
                <h3 className='font-serif text-[20px] leading-[22px] text-gray-800'>
                  {ev.title}
                </h3>
                {ev.speaker && (
                  <div className='flex items-center gap-3'>
                    <div className='flex size-10 items-center justify-center rounded-full bg-gray-200 text-[10px] text-gray-400'>
                      講師
                    </div>
                    <div className='min-w-0'>
                      <p className='text-sm font-semibold text-gray-800'>
                        {ev.speaker}
                      </p>
                      {ev.speakerTitle && (
                        <p className='text-xs text-gray-500'>
                          {ev.speakerTitle}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {ev.description && (
                  <p className='line-clamp-3 text-sm leading-6 text-gray-500'>
                    {ev.description}
                  </p>
                )}
                <div className='flex items-center gap-3 pt-1'>
                  <Link
                    href={`/events/${ev.id}`}
                    className='inline-flex h-10 flex-1 items-center justify-center border-2 border-secondary px-4 text-sm font-semibold text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
                  >
                    詳細を見る
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* ページネーション（membersはクライアント取得のためここで描画） */}
      <div className='mt-10 flex items-center justify-center gap-2'>
        {currentPage > 1 && (
          <Link
            href={makeHref(currentPage - 1)}
            className='grid size-10 place-items-center rounded-full border border-line bg-white text-primary'
            aria-label='前のページへ'
          >
            ‹
          </Link>
        )}
        {renderPagination({
          currentPage,
          totalPages,
          makeHref,
        })}
        {currentPage < totalPages && (
          <Link
            href={makeHref(currentPage + 1)}
            className='grid size-10 place-items-center rounded-full border border-line bg-white text-primary'
            aria-label='次のページへ'
          >
            ›
          </Link>
        )}
      </div>
    </>
  );
}

function renderPagination({
  currentPage,
  totalPages,
  makeHref,
}: {
  currentPage: number;
  totalPages: number;
  makeHref: (p: number) => string;
}) {
  const btn = (p: number, label?: string) => (
    <Link
      key={`p-${p}-${label ?? ''}`}
      href={makeHref(p)}
      className={`grid size-10 place-items-center rounded-full ${
        p === currentPage
          ? 'bg-primary text-white'
          : 'border border-line bg-white text-gray-600'
      }`}
      aria-current={p === currentPage ? 'page' : undefined}
      aria-label={label ? `ページ ${label}` : `ページ ${p}`}
    >
      {label ?? p}
    </Link>
  );

  const items: any[] = [];
  const pushRange = (a: number, b: number) => {
    for (let i = a; i <= b; i++) items.push(btn(i));
  };
  if (totalPages <= 7) {
    pushRange(1, totalPages);
  } else {
    items.push(btn(1));
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2)
      items.push(
        <span key='dots-l' className='px-2 text-sm text-gray-400'>
          …
        </span>
      );
    pushRange(start, end);
    if (end < totalPages - 1)
      items.push(
        <span key='dots-r' className='px-2 text-sm text-gray-400'>
          …
        </span>
      );
    items.push(btn(totalPages));
  }
  return <>{items}</>;
}

/**
 * 日付(M/D)をJST（Asia/Tokyo）基準でフォーマット
 * - 目的: サーバ/ブラウザのローカルTZに依存せず、常にJSTで表示する
 * - 引数: Date | null
 * - 戻り値: 文字列（例: "3/20"）
 */
function formatMonthDayJST(date: Date | null): string {
  if (!date) return '';
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${m}/${d}`;
}

